import posthog from 'posthog-js'

// PostHog project (public, client-side) API key. Safe to ship in the bundle —
// it's a write-only ingest key, the same posture as the Clarity tag in
// index.html. Overridable per environment via VITE_POSTHOG_KEY so staging /
// preview builds can point at a different project (or disable by setting it
// empty). The default keeps prod analytics working out of the box.
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY ?? 'phc_nq9CwhwKB2pFiPRHYUfCIWfprRPPq8v215BHrXuBQwo'
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST ?? 'https://us.i.posthog.com'

let started = false

/**
 * Initialise PostHog once, at app boot. No-ops if the key is blank (e.g. a
 * build that opts out) or if called more than once. Pageviews are captured
 * manually on route change (see capturePageview) since this is a client-side
 * router, so pageview autocapture is disabled to avoid double counting.
 *
 * Privacy posture: events are sent through our reverse proxy (api_host), but the
 * proxy only relays the payload — it does not strip PHI. During the closed beta
 * there is no real patient PHI in the product, so session recording is ON to
 * help improve the app. Autocapture and heatmaps stay off, and URL query strings
 * are stripped, as baseline hygiene.
 *
 * TODO (before GA / real PHI — see PEL-39): session recording must be re-locked
 * down or run with aggressive masking (session_recording.maskAllInputs +
 * maskTextSelector, ph-no-capture on clinical elements) before any real patient
 * data is on screen, since the proxy does not scrub PHI from recordings.
 */
export function initAnalytics(): void {
  if (started || typeof window === 'undefined') return
  if (!POSTHOG_KEY) return
  started = true

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    // Send ingest through the proxy, but keep toolbar / "view in PostHog" links
    // pointed at the real app UI.
    ui_host: 'https://us.posthog.com',
    person_profiles: 'identified_only',
    capture_pageview: false,
    capture_pageleave: true,
    // --- Implicit-collection controls ---
    autocapture: false, // no DOM click/text/label/aria scraping
    disable_session_recording: false, // beta: record the screen (no real PHI yet)
    capture_heatmaps: false, // DOM-position capture, rides on autocapture
    // Belt-and-suspenders: drop query strings / hashes off URL properties so a
    // stray token or patient param can't ride along in $current_url etc.
    sanitize_properties: (properties) => {
      const stripUrl = (value: unknown): unknown => {
        if (typeof value !== 'string') return value
        try {
          const url = new URL(value)
          return url.origin + url.pathname
        } catch {
          return value
        }
      }
      if (properties.$current_url) properties.$current_url = stripUrl(properties.$current_url)
      if (properties.$referrer) properties.$referrer = stripUrl(properties.$referrer)
      return properties
    },
  })
}

/**
 * Tie subsequent events to a known user. Called once the current user loads.
 * Uses the opaque numeric user id as the distinct id and sends no PII (no
 * email/name) — HIPAA / privacy posture. user_type is a non-PII role kept for
 * segmentation.
 */
export function identifyUser(user: {
  id: number
  user_type?: string | null
}): void {
  if (!started) return
  posthog.identify(String(user.id), {
    user_type: user.user_type ?? undefined,
  })
}

/** Clear the identified user on sign-out so the next session starts anonymous. */
export function resetAnalytics(): void {
  if (!started) return
  posthog.reset()
}

/** Fire a custom product event. Silently no-ops until PostHog is initialised. */
export function captureEvent(event: string, properties?: Record<string, unknown>): void {
  if (!started) return
  posthog.capture(event, properties)
}

/** Record a SPA pageview. Call on every router location change. */
export function capturePageview(): void {
  if (!started) return
  posthog.capture('$pageview')
}
